import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  MdChatBubbleOutline,
  MdDarkMode,
  MdLightMode,
  MdLogout,
  MdMenu,
  MdNotificationsNone,
  MdPersonOutline,
} from "react-icons/md";
import { getTextSize, setTextSize, TEXT_SIZES } from "../../utils/textsize";

/**
 * 頁首 —— MUI AppBar（Material Design 3 的 top app bar）。
 *
 * 右側只放圖示鈕，名字在 tooltip 與 aria-label；IconButton 的尺寸走 --t-control-h（≥ 44px），
 * 大字模式會跟著放大。未讀數用 Badge 掛在圖示角落。
 *
 * 字級（小／中／大）與顏色主題（淺色／深色）收在最右邊的帳號下拉裡。
 * 下拉用 Popover 而不是 Menu：Menu 按 Tab 會直接關掉，鍵盤使用者就碰不到裡面的切換鈕。
 *
 * 手機版（compact，Layout 在 !wide 時傳進來）：
 *   · 最左邊多一顆「選單」打開側邊抽屜；桌機側欄固定是收合態，不需要這顆。
 *   · 「聊天」移進側邊抽屜（Sider.js），頁首不畫；聊天未讀數掛在「選單」上。
 *   · 標題與副標各自一行省略，按鈕一顆都不能被擠出畫面。
 */

/* 帳號下拉裡「顏色主題」切換鈕的圖示 */
const SUN_ICON = <MdLightMode aria-hidden="true" />;
const MOON_ICON = <MdDarkMode aria-hidden="true" />;

/* 聊天圖示。手機版聊天鈕搬進側邊抽屜，Sider.js 從這裡 import 同一份，桌機與手機長得一樣 */
export const CHAT_ICON = <MdChatBubbleOutline aria-hidden="true" />;

/* 未讀數封頂 99+，實際筆數在面板裡看得到。抽屜裡的聊天未讀數也用這一份。 */
export const fmtCount = (n) => (n > 99 ? "99+" : String(n));

/** 頁首的圖示鈕：tooltip 顯示中文名稱，count > 0 時掛徽章 */
function TopIcon({ label, ariaLabel, count, onClick, buttonRef, children, ...rest }) {
  return (
    <Tooltip title={label}>
      <IconButton ref={buttonRef} aria-label={ariaLabel || label} onClick={onClick} color="inherit" {...rest}>
        <Badge badgeContent={count > 0 ? fmtCount(count) : null} color="error">
          {children}
        </Badge>
      </IconButton>
    </Tooltip>
  );
}

TopIcon.propTypes = {
  label: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string,
  count: PropTypes.number,
  onClick: PropTypes.func,
  buttonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  children: PropTypes.node,
};

TopIcon.defaultProps = { ariaLabel: "", count: 0, onClick: () => {}, buttonRef: null, children: null };

function Header({
  pageTitle,
  pageSub,
  userName,
  isDark,
  onToggleTheme,
  unreadCount,
  onOpenNotif,
  chatUnread,
  onOpenChat,
  compact,
  navOpen,
  onToggleNav,
  onLogout,
}) {
  const navigate = useNavigate();
  const menuBtnRef = useRef(null);
  const wasNavOpen = useRef(navOpen);
  const [accountEl, setAccountEl] = useState(null);
  /* 字級的真正來源是 <html data-textsize>（偏好設定頁也會改），這裡跟著它走 */
  const [textSize, setTextSizeState] = useState(() => getTextSize());
  useEffect(() => {
    const mo = new MutationObserver(() => setTextSizeState(getTextSize()));
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-textsize"] });
    return () => mo.disconnect();
  }, []);

  /*
   * 抽屜關起來時把焦點還給「選單」鈕：抽屜一卸載，焦點會掉回 <body>，
   * 鍵盤使用者得從頁面最上面重新 Tab 過來。只在焦點確實掉到 body 時才接手。
   */
  useEffect(() => {
    const btn = menuBtnRef.current;
    const active = document.activeElement;
    if (wasNavOpen.current && !navOpen && btn && (!active || active === document.body)) {
      btn.focus();
    }
    wasNavOpen.current = navOpen;
  }, [navOpen]);

  const nameLabel = userName || "我的帳號";
  const closeAccount = () => setAccountEl(null);
  const pick = (fn) => () => {
    closeAccount();
    fn();
  };

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{ bgcolor: "background.default", borderBottom: 1, borderColor: "divider", zIndex: 20, flexShrink: 0 }}
    >
      <Toolbar sx={{ gap: 1, minHeight: { xs: 64 }, px: { xs: 2, md: 3 } }}>
        {compact ? (
          <TopIcon
            buttonRef={menuBtnRef}
            label="選單"
            ariaLabel={chatUnread > 0 ? `選單，聊天未讀 ${fmtCount(chatUnread)} 則` : "選單"}
            aria-expanded={navOpen}
            count={chatUnread}
            onClick={onToggleNav}
            edge="start"
          >
            <MdMenu />
          </TopIcon>
        ) : null}

        {/* 標題與副標：沒有副標時 Layout 傳空字串，就不渲染（空的東西不要畫出來） */}
        <Box
          sx={{
            flex: "1 1 auto",
            minWidth: 0,
            display: "flex",
            flexDirection: compact ? "column" : "row",
            alignItems: compact ? "flex-start" : "baseline",
            gap: compact ? 0 : 1.5,
          }}
        >
          <Typography variant="h2" component="h1" noWrap sx={{ maxWidth: "100%" }}>
            {pageTitle}
          </Typography>
          {pageSub ? (
            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: "100%" }}>
              {pageSub}
            </Typography>
          ) : null}
        </Box>

        <TopIcon
          label="通知"
          ariaLabel={unreadCount > 0 ? `通知，未讀 ${fmtCount(unreadCount)} 則` : "通知"}
          count={unreadCount}
          onClick={onOpenNotif}
        >
          <MdNotificationsNone />
        </TopIcon>

        {compact ? null : (
          <TopIcon
            label="聊天"
            ariaLabel={chatUnread > 0 ? `聊天，未讀 ${fmtCount(chatUnread)} 則` : "聊天"}
            count={chatUnread}
            onClick={onOpenChat}
          >
            {CHAT_ICON}
          </TopIcon>
        )}

        {/* 帳號選單：沒有拿到姓名時退回「我的帳號」—— 不要用 '?' 或帳號 ID 充數 */}
        <TopIcon
          label={nameLabel}
          aria-haspopup="dialog"
          aria-expanded={accountEl ? true : undefined}
          aria-controls={accountEl ? "account-menu" : undefined}
          onClick={(e) => setAccountEl(e.currentTarget)}
          edge="end"
        >
          <MdPersonOutline />
        </TopIcon>
        <Popover
          id="account-menu"
          anchorEl={accountEl}
          open={!!accountEl}
          onClose={closeAccount}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { width: 300, maxWidth: "calc(100vw - 32px)", py: 1 } } }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1, pb: 0.5, fontWeight: 600 }}>
            {nameLabel}
          </Typography>
          <List disablePadding>
            <ListItemButton onClick={pick(() => navigate("/Me"))}>
              <ListItemIcon sx={{ mr: 1.5 }}>
                <MdPersonOutline />
              </ListItemIcon>
              <ListItemText primary="我的帳號" />
            </ListItemButton>
          </List>

          <Divider sx={{ my: 1 }} />

          {/* 字級與顏色主題：切了當場生效，不關下拉，方便來回比較 */}
          <Box sx={{ px: 2, py: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box>
              <Typography id="acct-textsize" variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.75 }}>
                字級
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                color="primary"
                value={textSize}
                onChange={(_e, v) => {
                  /* 值 "" 是「中」；再按一次已選的會回 null，擋掉 */
                  if (v !== null) setTextSizeState(setTextSize(v));
                }}
                aria-labelledby="acct-textsize"
              >
                {TEXT_SIZES.map((o) => (
                  <ToggleButton key={o.label} value={o.value} aria-label={`字級：${o.label}`} sx={{ fontSize: o.preview }}>
                    {o.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Box>
              <Typography id="acct-theme" variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.75 }}>
                顏色主題
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                color="primary"
                value={isDark ? "dark" : "light"}
                onChange={(_e, v) => {
                  if (v && v !== (isDark ? "dark" : "light")) onToggleTheme();
                }}
                aria-labelledby="acct-theme"
              >
                <ToggleButton value="light" sx={{ gap: 1 }}>
                  {SUN_ICON}
                  淺色
                </ToggleButton>
                <ToggleButton value="dark" sx={{ gap: 1 }}>
                  {MOON_ICON}
                  深色
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          <Divider sx={{ my: 1 }} />

          <List disablePadding>
            <ListItemButton onClick={pick(onLogout)}>
              <ListItemIcon sx={{ mr: 1.5 }}>
                <MdLogout />
              </ListItemIcon>
              <ListItemText primary="登出" />
            </ListItemButton>
          </List>
        </Popover>
      </Toolbar>
    </AppBar>
  );
}

Header.propTypes = {
  pageTitle: PropTypes.string,
  pageSub: PropTypes.string,
  userName: PropTypes.string,
  isDark: PropTypes.bool,
  onToggleTheme: PropTypes.func,
  unreadCount: PropTypes.number,
  onOpenNotif: PropTypes.func,
  chatUnread: PropTypes.number,
  onOpenChat: PropTypes.func,
  compact: PropTypes.bool,
  navOpen: PropTypes.bool,
  onToggleNav: PropTypes.func,
  onLogout: PropTypes.func,
};

Header.defaultProps = {
  pageTitle: "",
  pageSub: "",
  userName: "",
  isDark: false,
  onToggleTheme: () => {},
  unreadCount: 0,
  onOpenNotif: () => {},
  chatUnread: 0,
  onOpenChat: () => {},
  compact: false,
  navOpen: false,
  onToggleNav: () => {},
  onLogout: () => {},
};

export default Header;
